import { combineReducers, createReducer } from '@reduxjs/toolkit';
import { APP_USE_FULLSCREEN, MNS_CONNECTION_STATE_CHANGE, OVERRIDE } from '../actions/actionTypes';
import {
  appLoaded, appReady, appStarted, interactiveCreate, interactivePause, interactiveResume, interactiveStart,
  interactiveStop, setDebug
} from '../actions/actions';

/**
 * @typedef {Object} AppState
 *
 * @property {boolean} loaded
 * @property {boolean} ready
 * @property {boolean} paused
 * @property {boolean} visible
 * @property {boolean} hidden
 * @property {boolean} started
 * @property {boolean} mnsConnected
 * @property {boolean} schedulerConnected
 * @property {string} jwtToken
 * @property {string} sessionId
 * @property {[string]} override
 * @property {boolean} useFullscreen
 * @property {string} contentRequestTopic
 */

const noop = (_builder) => {};
/**
 * @name appState
 * @type {AppState}
 */
export default combineReducers({
  version: createReducer(import.meta.env.VITE_APP_VERSION, noop),
  build: createReducer(import.meta.env.VITE_BUILD_LABEL, noop),
  config: combineReducers({
    liveThreshold: createReducer(12, (builder) => {
      builder.addCase(interactiveCreate, (state, action) => {
        return action.payload.mns_config.min_history_time_diff;
      });
    }),
    eventsRequestInterval: createReducer(
      60 * 60, // 1 hour
      // 60 * 60 * 24 // 1 day
      noop,
    ),
  }),
  contentRequestTopic: createReducer(null, (builder) => {
    builder.addCase(interactiveStart, (state, action) => {
      return action.payload.config.content_request;
    });
  }),
  debug: createReducer(false, (builder) => {
    builder.addCase(setDebug, (state, action) => {
      return action.payload;
    });
  }),
  loaded: createReducer(false, (builder) => {
      // TODO: make consistent with all other
    builder.addCase(appLoaded, (state, action) => action.payload);
  }),
  ready: createReducer(false, (builder) => {
    builder.addCase(appReady, () => true);
    builder.addCase(MNS_CONNECTION_STATE_CHANGE, (state, action)  => {
      /*
      ready, wsConnected => result
      false, false => false
      false, true => true
      true, false => true
      true, true => true
       */
      return state || action.payload.isConnected;
    });
  }),
  suspended: createReducer(true, (builder) => {
    builder.addCase(interactiveStart, () => false);
    builder.addCase(interactiveStop, () => true);
  }),
  paused: createReducer(false, (builder) => {
    builder.addCase(interactivePause, () => true);
    builder.addCase(interactiveResume, () => false);
  }),
  visible: createReducer(true, (builder) => {
    builder.addCase(interactivePause, () => false);
    builder.addCase(interactiveResume, () => true);
    builder.addCase(appStarted, () => true);
  }),
  hidden: createReducer(false, (builder) => {
    builder.addCase(interactivePause, () => true);
    builder.addCase(interactiveResume, () => false);
    builder.addCase(appStarted, () => false);
  }),
  override: createReducer([], (builder )=> {
    builder.addCase(OVERRIDE, (state, action) => action.payload);
    builder.addCase(interactiveCreate, (state, action) => {
      const { override = [] } = action.payload;
      return override;
    });
  }),
  useFullscreen: createReducer(false, (builder) => {
    builder.addCase(APP_USE_FULLSCREEN, (state, action) => action.payload.isFullscreen);
  }),
  sessionId: createReducer('', (builder) => {
    builder.addCase(interactiveCreate, (state, action) => action.payload.mns_config.client_id);
  }),
});
